import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import CotizacionesColumn from './columns/CotizacionesColumn';
import EnCursoColumn from './columns/EnCursoColumn';
import FinalizadasColumn from './columns/FinalizadasColumn';
import RechazadasColumn from './columns/RechazadasColumn';
import { COLUMN_BUCKETS } from './columns/columnActions';

/**
 * Tablero Kanban con 4 columnas para solicitudes aéreas.
 *
 * Componente "slim": solo agrupa los datos y los reparte a las
 * 4 columnas. Cada columna es un archivo independiente
 * (`columns/*.jsx`) para que 2+ personas puedan trabajarlas en
 * paralelo sin pisarse los cambios en git.
 *
 * Props:
 *  - data: array de solicitudes
 *  - onAction: (actionKey, row) => void
 *  - searchTerm: término de búsqueda (filtra en cliente)
 *  - customBuckets: opcional, override de los buckets por columna
 *  - loading: indica carga en curso
 *  - boardRouteByEstado: mapa estado → ruta del tablero detallado.
 *    Cuando está, el header de la columna muestra un botón
 *    "Ver tablero →" que navega a esa ruta.
 *
 * Las columnas y los estados que agrupan son (definidos en
 * `columns/columnActions.js → COLUMN_BUCKETS`):
 *  - Cotizaciones         → Borrador
 *  - Operaciones en curso → Enviada
 *  - Finalizadas          → Aprobada
 *  - Rechazadas           → Rechazada
 */

const COLUMN_COMPONENTS = {
  cotizaciones: CotizacionesColumn,
  'en-curso': EnCursoColumn,
  finalizadas: FinalizadasColumn,
  rechazadas: RechazadasColumn,
};

const matchesBucket = (row, estados) => {
  const estado = String(row?.estado || '').trim();
  if (!estado) return false;
  return estados.some((e) => e.toLowerCase() === estado.toLowerCase());
};

const matchesSearch = (row, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  return [
    row?.codigo,
    row?._id,
    row?.clienteSnapshot?.nombre,
    row?.clienteSnapshot?.nit,
    row?.asesorSnapshot?.nombre,
    row?.tipoOperacion,
    row?.paisOrigen,
    row?.paisDestino,
    row?.aeropuertoCargaSnapshot?.nombre,
    row?.aeropuertoCargaSnapshot?.ciudad,
    row?.aeropuertoCargaSnapshot?.iata,
    row?.aeropuertoDescargaSnapshot?.nombre,
    row?.aeropuertoDescargaSnapshot?.ciudad,
    row?.aeropuertoDescargaSnapshot?.iata,
    row?.incotermSnapshot?.codigo,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(t));
};

/**
 * Botón "Ver tablero →" que se inyecta en el `headerExtra` de cada
 * columna cuando hay un `boardRouteByEstado` configurado por el padre.
 *
 * Al hacer click, captura el `getBoundingClientRect()` de la columna
 * (el contenedor padre con `data-column-id`) y lo pasa en
 * `location.state.fromRect` para que `BoardPageTransition` haga
 * la animación de "wipe" desde esa posición.
 */
const BoardLink = ({ bucket, boardRouteByEstado, navigate }) => {
  const boardRoute = boardRouteByEstado
    ? boardRouteByEstado[bucket.estados[0]]
    : null;
  if (!boardRoute) return null;
  const handleClick = (e) => {
    e.stopPropagation();
    const colEl = e.currentTarget.closest('[data-column-id]');
    const rect = colEl ? colEl.getBoundingClientRect() : null;
    navigate(boardRoute, {
      state: rect
        ? {
            fromRect: {
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            },
          }
        : null,
    });
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition"
      title={`Ir al tablero de ${bucket.title}`}
    >
      Ver tablero
      <ArrowRight className="h-3 w-3" />
    </button>
  );
};

const OperationKanban = ({
  data = [],
  onAction,
  searchTerm = '',
  customBuckets,
  loading = false,
  boardRouteByEstado,
}) => {
  const navigate = useNavigate();
  const buckets = customBuckets || COLUMN_BUCKETS;

  const grouped = useMemo(() => {
    const out = {};
    for (const b of buckets) {
      out[b.id] = data.filter(
        (row) => matchesBucket(row, b.estados) && matchesSearch(row, searchTerm),
      );
    }
    return out;
  }, [data, buckets, searchTerm]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {buckets.map((bucket) => {
        const items = grouped[bucket.id] || [];
        const ColumnComponent = COLUMN_COMPONENTS[bucket.id];
        if (!ColumnComponent) return null;
        return (
          <ColumnComponent
            key={bucket.id}
            items={items}
            onAction={onAction}
            loading={loading}
            actionsMode="view"
            headerExtra={
              <BoardLink
                bucket={bucket}
                boardRouteByEstado={boardRouteByEstado}
                navigate={navigate}
              />
            }
          />
        );
      })}
    </div>
  );
};

export default OperationKanban;
